// src/pages/SignInPage.jsx
import React from "react";
import { Box, Button, Link, Stack, Typography, Alert } from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import AuthShell from "./components/AuthShell.jsx";
import AuthBrandPanel from "./components/AuthBrandPanel.jsx";
import AuthFormHeader from "./components/AuthFormHeader.jsx";
import AuthField from "./components/AuthField.jsx";
import {
  AUTH_BRAND_CONTENT,
  AUTH_FORM_HEADERS,
} from "./components/authContents";
import { primaryBtnSx } from "./components/authStyles";

import { supabase } from "../../components/SupabaseClient";

function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const trimmedEmail = email.trim();

    // Per-field required validation (matches the sibling auth pages).
    const emailErr = trimmedEmail ? "" : "Email is required.";
    const passwordErr = password ? "" : "Password is required.";
    setEmailError(emailErr);
    setPasswordError(passwordErr);

    if (emailErr || passwordErr) {
      // Keep the top-level Alert too for parity with the prior behavior.
      setErrorMsg("Email and password are required.");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });
      if (error) {
        const normalized = /invalid login/i.test(error.message)
          ? "Invalid email or password."
          : error.message || "Failed to sign in. Please try again.";
        throw new Error(normalized);
      }

      const requestedRedirect = searchParams.get("redirect");
      let safeRedirect = "/";
      if (requestedRedirect?.startsWith("/")) {
        try {
          const redirectUrl = new URL(
            requestedRedirect,
            window.location.origin,
          );
          if (redirectUrl.origin === window.location.origin) {
            safeRedirect = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
          }
        } catch {
          // Keep the default for malformed redirect values.
        }
      }
      navigate(safeRedirect, { replace: true });
    } catch (err) {
      setErrorMsg(err.message || "Failed to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        bgcolor: "#EEF1F0",
      }}
    >
      <AuthShell brand={<AuthBrandPanel {...AUTH_BRAND_CONTENT.signIn} />}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <AuthFormHeader {...AUTH_FORM_HEADERS.signIn} />

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <AuthField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Enter your email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            error={emailError}
            startIcon={<MailOutlineIcon fontSize="small" />}
          />

          <AuthField
            label="Password"
            labelProps={{ mt: 2 }}
            name="password"
            autoComplete="current-password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
            }}
            error={passwordError}
            password
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword((v) => !v)}
            startIcon={<LockOutlinedIcon fontSize="small" />}
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              underline="hover"
            >
              Forgot Password?
            </Link>
          </Stack>

          <Button type="submit" fullWidth disabled={loading} sx={primaryBtnSx}>
            {loading ? "Signing in…" : "Sign In"}
          </Button>

          <Typography align="center" variant="body2" sx={{ mt: 3 }}>
            Don&apos;t have an account?{" "}
            <Link
              component={RouterLink}
              to="/signup"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              Sign Up
            </Link>
          </Typography>
        </Box>
      </AuthShell>
    </Box>
  );
}

export default SignInPage;
